import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import {
  Box, Typography, Card, CardContent, Grid, Alert, Fade, Chip, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TablePagination, TextField, InputAdornment, IconButton, Skeleton, Button,
  useTheme, FormControl, InputLabel, Select, MenuItem, alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  PregnantWoman as PregnantIcon,
  Accessible as AccessibleIcon,
  SmokingRooms as SmokingIcon,
  LocalBar as AlcoholIcon,
  Hotel as BedIcon,
  CardGiftcard as BenefitIcon,
  HealthAndSafety as HealthIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell, Legend, PieChart, Pie,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { saudeService } from '../../services/api';

// ─── Types ───

interface Equipe {
  ine: string;
  nome: string;
}

interface FaixaEtaria {
  faixa: string;
  masc: number;
  fem: number;
}

interface DistItem {
  label: string;
  total: number;
}

interface Resumo {
  indicadores: {
    totalCidadaos: number;
    masc: number;
    fem: number;
    gestantes: number;
    fumantes: number;
    alcool: number;
    acamados: number;
    domiciliados: number;
    beneficioSocial: number;
    planoPrivado: number;
    pcdTotal: number;
  };
  porFaixaEtaria: FaixaEtaria[];
  porRacaCor: DistItem[];
  porEscolaridade: DistItem[];
  porSituacaoTrabalho: DistItem[];
  condicoesCronicas: Record<string, number>;
  deficiencias: Record<string, number>;
  timestamp: string;
}

// ─── Colors ───

const COLORS = ['#1976d2', '#e53935', '#43a047', '#ff9800', '#7b1fa2', '#00838f', '#6d4c41', '#546e7a', '#d81b60', '#f57c00'];

const CRONICAS_LABELS: Record<string, string> = {
  hipertensos: 'Hipertensao',
  diabeticos: 'Diabetes',
  respiratorios: 'D. Respiratoria',
  cardiacos: 'D. Cardiaca',
  cancer: 'Cancer',
  tuberculose: 'Tuberculose',
  hanseniase: 'Hanseniase',
  avc: 'AVC',
  infarto: 'Infarto',
  rins: 'D. Renal',
};

const DEFIC_LABELS: Record<string, string> = {
  auditiva: 'Auditiva',
  visual: 'Visual',
  fisica: 'Fisica',
  intelectual: 'Intelectual',
  tea: 'TEA',
};

// ─── Component ───

const SaudeDadosSociodemograficos: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resumo, setResumo] = useState<Resumo | null>(null);

  // Equipe filter
  const [equipes, setEquipes] = useState<Equipe[]>([]);
  const [selectedIne, setSelectedIne] = useState('');

  // Nominal tab
  const [nominalLoading, setNominalLoading] = useState(false);
  const [nominalData, setNominalData] = useState<any>(null);
  const [nominalPage, setNominalPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // ─── Data fetching ───

  const fetchResumo = useCallback(async (ine?: string) => {
    if (!municipioAtivo?.id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await saudeService.getDadosSocioResumo(municipioAtivo.id, ine || undefined);
      // DWH agora suportado
      setResumo(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar dados sociodemograficos');
    } finally {
      setLoading(false);
    }
  }, [municipioAtivo?.id]);

  const fetchEquipes = useCallback(async () => {
    if (!municipioAtivo?.id) return;
    try {
      const data = await saudeService.getDadosSocioEquipes(municipioAtivo.id);
      setEquipes(data || []);
    } catch { /* non-blocking */ }
  }, [municipioAtivo?.id]);

  const fetchNominal = useCallback(async (page = 0, ine?: string) => {
    if (!municipioAtivo?.id) return;
    setNominalLoading(true);
    try {
      const data = await saudeService.getDadosSocioNominal(municipioAtivo.id, page + 1, 50, ine || undefined);
      setNominalData(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar lista nominal');
    } finally {
      setNominalLoading(false);
    }
  }, [municipioAtivo?.id]);

  useEffect(() => {
    fetchResumo();
    fetchEquipes();
  }, [fetchResumo, fetchEquipes]);

  // When switching to nominal tab, load data if not loaded yet
  useEffect(() => {
    if (tab === 1 && !nominalData && !nominalLoading) {
      fetchNominal(0, selectedIne);
    }
  }, [tab, nominalData, nominalLoading, fetchNominal, selectedIne]);

  const handleIneChange = (ine: string) => {
    setSelectedIne(ine);
    setNominalPage(0);
    setNominalData(null);
    fetchResumo(ine);
    if (tab === 1) fetchNominal(0, ine);
  };

  const handleNominalPageChange = (_: unknown, newPage: number) => {
    setNominalPage(newPage);
    fetchNominal(newPage, selectedIne);
  };

  const handleExport = async () => {
    if (!municipioAtivo?.id) return;
    try {
      await saudeService.exportarDadosSocioCsv(municipioAtivo.id, selectedIne || undefined);
    } catch {
      setError('Erro ao exportar CSV');
    }
  };

  const handleRefresh = () => {
    fetchResumo(selectedIne);
    if (tab === 1) {
      setNominalPage(0);
      fetchNominal(0, selectedIne);
    }
  };

  const filteredRows = useMemo(() => {
    if (!nominalData?.registros) return [];
    if (!searchTerm) return nominalData.registros;
    const term = searchTerm.toUpperCase();
    return nominalData.registros.filter((r: any) =>
      Object.values(r).some(v => v && String(v).toUpperCase().includes(term))
    );
  }, [nominalData, searchTerm]);

  // ─── Derived chart data ───

  const pyramidData = useMemo(() => {
    if (!resumo) return [];
    return resumo.porFaixaEtaria.map(f => ({
      faixa: f.faixa,
      masc: -f.masc, // negative for left side
      fem: f.fem,
      mascAbs: f.masc,
    }));
  }, [resumo]);

  const cronicasChartData = useMemo(() => {
    if (!resumo) return [];
    return Object.entries(resumo.condicoesCronicas)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => ({ label: CRONICAS_LABELS[k] || k, total: v }));
  }, [resumo]);

  const deficienciasChartData = useMemo(() => {
    if (!resumo) return [];
    return Object.entries(resumo.deficiencias)
      .filter(([k, v]) => k !== 'total' && v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => ({ label: DEFIC_LABELS[k] || k, total: v }));
  }, [resumo]);

  if (!municipioAtivo) {
    return <Box sx={{ p: 3 }}><Alert severity="warning">Nenhum municipio selecionado</Alert></Box>;
  }

  const ind = resumo?.indicadores;

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Fade in timeout={600}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Perfil Demografico</Typography>
            <Typography variant="body2" color="text.secondary">
              {municipioAtivo.nome} - Dados sociodemograficos da populacao cadastrada
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Equipe filter */}
            {equipes.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Equipe</InputLabel>
                <Select value={selectedIne} label="Equipe" onChange={e => handleIneChange(e.target.value)}>
                  <MenuItem value="">Todas as equipes</MenuItem>
                  {equipes.map(eq => (
                    <MenuItem key={eq.ine} value={eq.ine}>{eq.nome} ({eq.ine})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {tab === 1 && (
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>CSV</Button>
            )}
            <IconButton onClick={handleRefresh} size="small" title="Atualizar"><RefreshIcon /></IconButton>
          </Box>
        </Box>
      </Fade>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Visao Geral" />
        <Tab label="Lista Nominal" />
      </Tabs>

      {/* ═══ TAB 0: Visao Geral ═══ */}
      {tab === 0 && (
        <Fade in timeout={600}>
          <Box>
            {loading ? (
              <Grid container spacing={2}>
                {[...Array(8)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                    <Skeleton variant="rounded" height={120} />
                  </Grid>
                ))}
                <Grid item xs={12} md={6}><Skeleton variant="rounded" height={400} /></Grid>
                <Grid item xs={12} md={6}><Skeleton variant="rounded" height={400} /></Grid>
              </Grid>
            ) : resumo && ind ? (
              <>
                {/* Key Indicators Row */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={4} md={2}>
                    <MiniCard label="Total Cadastrados" value={ind.totalCidadaos} color="#1565c0" />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <MiniCard label="Masculino" value={ind.masc} color="#1976d2" icon={<MaleIcon />}
                      pct={ind.totalCidadaos > 0 ? ((ind.masc / ind.totalCidadaos) * 100).toFixed(1) + '%' : undefined} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <MiniCard label="Feminino" value={ind.fem} color="#e91e63" icon={<FemaleIcon />}
                      pct={ind.totalCidadaos > 0 ? ((ind.fem / ind.totalCidadaos) * 100).toFixed(1) + '%' : undefined} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <MiniCard label="Gestantes" value={ind.gestantes} color="#7b1fa2" icon={<PregnantIcon />} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <MiniCard label="PCD" value={ind.pcdTotal} color="#00695c" icon={<AccessibleIcon />} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <MiniCard label="Beneficio Social" value={ind.beneficioSocial} color="#e65100" icon={<BenefitIcon />} />
                  </Grid>
                </Grid>

                {/* Secondary indicators */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3} md={2}>
                    <MiniCard label="Fumantes" value={ind.fumantes} color="#795548" icon={<SmokingIcon />} small />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <MiniCard label="Uso Alcool" value={ind.alcool} color="#bf360c" icon={<AlcoholIcon />} small />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <MiniCard label="Acamados" value={ind.acamados} color="#4527a0" icon={<BedIcon />} small />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <MiniCard label="Domiciliados" value={ind.domiciliados} color="#1565c0" icon={<BedIcon />} small />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <MiniCard label="Plano Privado" value={ind.planoPrivado} color="#2e7d32" icon={<HealthIcon />} small />
                  </Grid>
                </Grid>

                {/* Charts Row 1: Piramide + Raca/Cor */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={7}>
                    <ChartCard title="Piramide Etaria">
                      <ResponsiveContainer width="100%" height={420}>
                        <BarChart data={pyramidData} layout="vertical" stackOffset="sign" margin={{ left: 10, right: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tickFormatter={v => Math.abs(v).toLocaleString('pt-BR')} />
                          <YAxis type="category" dataKey="faixa" width={40} tick={{ fontSize: 11 }} />
                          <RechartsTooltip
                            formatter={(value: any, name: any) => [
                              Math.abs(Number(value)).toLocaleString('pt-BR'),
                              name === 'masc' ? 'Masculino' : 'Feminino',
                            ]}
                          />
                          <Legend formatter={v => v === 'masc' ? 'Masculino' : 'Feminino'} />
                          <Bar dataKey="masc" fill="#1976d2" stackId="stack" name="masc" />
                          <Bar dataKey="fem" fill="#e91e63" stackId="stack" name="fem" />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartCard>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <ChartCard title="Raca / Cor">
                      <ResponsiveContainer width="100%" height={420}>
                        <PieChart>
                          <Pie
                            data={resumo.porRacaCor}
                            dataKey="total"
                            nameKey="label"
                            cx="50%"
                            cy="50%"
                            outerRadius={130}
                            label={({ name, percent }: any) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
                            labelLine={{ strokeWidth: 1 }}
                          >
                            {resumo.porRacaCor.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip formatter={(v: any) => Number(v).toLocaleString('pt-BR')} />
                        </PieChart>
                      </ResponsiveContainer>
                    </ChartCard>
                  </Grid>
                </Grid>

                {/* Charts Row 2: Escolaridade + Situacao Trabalho */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <ChartCard title="Escolaridade">
                      <HorizontalBarChart data={resumo.porEscolaridade} color="#1565c0" />
                    </ChartCard>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ChartCard title="Situacao de Trabalho">
                      <HorizontalBarChart data={resumo.porSituacaoTrabalho} color="#2e7d32" />
                    </ChartCard>
                  </Grid>
                </Grid>

                {/* Charts Row 3: Cronicas + Deficiencias */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <ChartCard title="Condicoes Cronicas">
                      <HorizontalBarChart data={cronicasChartData} color="#e53935" />
                    </ChartCard>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ChartCard title="Deficiencias">
                      <HorizontalBarChart data={deficienciasChartData} color="#7b1fa2" />
                    </ChartCard>
                  </Grid>
                </Grid>

                {/* Timestamp */}
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Chip
                    label={`Atualizado: ${new Date(resumo.timestamp).toLocaleString('pt-BR')}`}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </>
            ) : null}
          </Box>
        </Fade>
      )}

      {/* ═══ TAB 1: Lista Nominal ═══ */}
      {tab === 1 && (
        <Fade in timeout={600}>
          <Box>
            {/* Search */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Buscar por nome, CPF, equipe..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
                sx={{ minWidth: 300 }}
              />
              {nominalData && (
                <Chip
                  label={`${nominalData.total.toLocaleString('pt-BR')} cidadaos`}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>

            {/* Table */}
            {nominalLoading ? (
              <Box>
                {[...Array(8)].map((_, i) => <Skeleton key={i} variant="rounded" height={44} sx={{ mb: 1 }} />)}
              </Box>
            ) : nominalData?.registros?.length > 0 ? (
              <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 340px)' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {Object.keys(nominalData.registros[0])
                        .filter(k => k !== 'id')
                        .map(key => (
                          <TableCell key={key} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {NOMINAL_HEADERS[key] || key}
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((row: any, idx: number) => (
                      <TableRow key={row.id || idx} hover>
                        {Object.entries(row)
                          .filter(([k]) => k !== 'id')
                          .map(([key, val], ci) => (
                            <TableCell key={ci} sx={{ whiteSpace: 'nowrap' }}>
                              {formatNominalCell(key, val)}
                            </TableCell>
                          ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">Nenhum registro encontrado</Alert>
            )}

            {nominalData && nominalData.total > 50 && (
              <TablePagination
                component="div"
                count={nominalData.total}
                page={nominalPage}
                onPageChange={handleNominalPageChange}
                rowsPerPage={50}
                rowsPerPageOptions={[50]}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            )}
          </Box>
        </Fade>
      )}
      {municipioAtivo && resumo && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(resumo)}
          municipioId={municipioAtivo.id}
          painelId="dados-sociodemograficos"
          manualTrigger
        />
      )}

      <ChatIAButton contexto="saude" dadosContexto={resumo ? JSON.stringify(resumo) : undefined} fabSx={{ bottom: 24, right: 24, top: 'auto' }} />
    </Box>
  );
};

// ─── Sub-components ───

const MiniCard: React.FC<{
  label: string;
  value: number;
  color: string;
  icon?: React.ReactNode;
  pct?: string;
  small?: boolean;
}> = ({ label, value, color, icon, pct, small }) => (
  <Card sx={{ borderTop: `3px solid ${color}`, height: '100%' }}>
    <CardContent sx={{ p: small ? 1.5 : 2, '&:last-child': { pb: small ? 1.5 : 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && <Box sx={{ color, opacity: 0.8 }}>{icon}</Box>}
        <Box sx={{ flex: 1 }}>
          <Typography variant={small ? 'h6' : 'h5'} fontWeight={700} sx={{ color }}>
            {value.toLocaleString('pt-BR')}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {label}
          </Typography>
        </Box>
        {pct && (
          <Chip label={pct} size="small" sx={{ backgroundColor: color + '18', color, fontWeight: 600, fontSize: 11 }} />
        )}
      </Box>
    </CardContent>
  </Card>
);

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card>
    <CardContent>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>{title}</Typography>
      {children}
    </CardContent>
  </Card>
);

const HorizontalBarChart: React.FC<{ data: DistItem[]; color: string }> = ({ data, color }) => {
  if (!data.length) return <Typography variant="body2" color="text.secondary">Sem dados</Typography>;

  const truncated = data.map(d => ({
    ...d,
    label: d.label.length > 30 ? d.label.slice(0, 28) + '...' : d.label,
    fullLabel: d.label,
  }));

  return (
    <ResponsiveContainer width="100%" height={Math.max(200, truncated.length * 36)}>
      <BarChart data={truncated} layout="vertical" margin={{ left: 10, right: 30 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tickFormatter={v => v.toLocaleString('pt-BR')} />
        <YAxis type="category" dataKey="label" width={160} tick={{ fontSize: 11 }} />
        <RechartsTooltip
          formatter={(v: any) => Number(v).toLocaleString('pt-BR')}
          labelFormatter={(label: any, payload: any) => payload?.[0]?.payload?.fullLabel || label}
        />
        <Bar dataKey="total" fill={color} radius={[0, 4, 4, 0]} barSize={20}>
          {truncated.map((_, i) => (
            <Cell key={i} fill={COLORS[i % COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// ─── Nominal table helpers ───

const NOMINAL_HEADERS: Record<string, string> = {
  nome: 'Nome',
  cpf: 'CPF',
  cns: 'CNS',
  dt_nascimento: 'Nascimento',
  idade: 'Idade',
  sexo: 'Sexo',
  raca_cor: 'Raca/Cor',
  escolaridade: 'Escolaridade',
  situacao_trabalho: 'Sit. Trabalho',
  equipe: 'Equipe',
  microarea: 'Microarea',
  telefone: 'Telefone',
  condicoes: 'Condicoes',
};

function formatNominalCell(key: string, val: any): React.ReactNode {
  if (val === null || val === undefined || val === '') return '-';

  if (key === 'dt_nascimento') {
    try { return new Date(val).toLocaleDateString('pt-BR'); } catch { return String(val); }
  }
  if (key === 'cpf' && val) {
    const s = String(val).padStart(11, '0');
    return `${s.slice(0, 3)}.${s.slice(3, 6)}.${s.slice(6, 9)}-${s.slice(9)}`;
  }
  if (key === 'sexo') {
    return val === 'M' ? 'Masc' : val === 'F' ? 'Fem' : val;
  }
  if (key === 'condicoes' && val) {
    const tags = String(val).split(', ').filter(Boolean);
    if (!tags.length) return '-';
    const tagColors: Record<string, string> = {
      HAS: '#e53935', DM: '#1565c0', GEST: '#7b1fa2', PCD: '#00695c',
      FUM: '#795548', ACAM: '#4527a0', DOM: '#1565c0', CA: '#d81b60',
      TB: '#e65100', HANS: '#f57c00', BF: '#2e7d32',
    };
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {tags.map(t => (
          <Chip
            key={t}
            label={t}
            size="small"
            variant="outlined"
            sx={{ fontSize: 10, height: 20, borderColor: tagColors[t] || '#757575', color: tagColors[t] || '#757575' }}
          />
        ))}
      </Box>
    );
  }
  return String(val);
}

export default SaudeDadosSociodemograficos;
